/**
 * PhoneFrame — chrome commun de TOUS les écrans candidat. Route rendue SANS
 * chrome cockpit (pas de TitleBar/SideRail, cf. App.tsx) — ce composant EST
 * tout le chrome de la page.
 *
 * SCROLL (fix 16-07) : le CSS global du cockpit verrouille le document
 * (`html[data-mobile] #root { position: fixed; overflow: hidden }`) → la page
 * ne peut JAMAIS scroller au niveau document. L'ancienne version comptait sur
 * le scroll du body : sur téléphone, tout écran plus haut que le viewport
 * était CLIPPÉ, CTA inatteignable (bug « ça tient pas » Kayla 16-07). Ce
 * composant est donc SON PROPRE scroller (`h-dvh overflow-y-auto`) — il
 * fonctionne quel que soit l'état du document.
 *
 * MOBILE-FIRST : une seule colonne (520 px au plus) sur le fond de la page,
 * du téléphone au grand écran — plus de « carte centrée » à contour gris : ce
 * sont les blocs du parcours qui sont des `Carte` de verre, la page ne l'est pas.
 *
 * PALETTE : celle du cockpit en sombre, noir et bleu (cf. `CANDIDATE_THEME`),
 * en verre (`data-theme="glass"`) : les surfaces de `@dozka/ui` (Carte, Champ,
 * Select…) prennent leur recette sombre et vitrée sur ce conteneur, quel que
 * soit le thème que le visiteur a choisi dans le cockpit — et la liste d'un
 * `Select`, portée sur <body>, recopie `.dark` + `data-theme` depuis cet
 * ancêtre pour rester sombre elle aussi.
 *
 * `TOTAL_STEPS` = 6, et c'est le nombre d'ecrans du parcours : vérif technique
 * → bienvenue → profil → brief → épreuve de vente → fin. Il valait encore 8
 * apres #497, qui a retire l'echauffement et la revue sans redescendre le
 * compte : la barre gardait deux segments que RIEN ne pouvait plus remplir, et
 * le candidat arrivait sur l'ecran final avec une barre aux trois quarts.
 */
import type { CSSProperties, ReactNode } from "react";

import { DEFAULT_BRAND, contrastFg, deriveHover } from "@dozka/ui";

import { Jauge } from "@/shared/ui";

/** Les positions de la jauge du parcours : vérifs → infos → épreuve → fin.
 *
 * 🪤 Doit rester d'accord avec les `step` de `SCREEN_META`
 * (`RegieTestCandidatePage`). C'était 6 jusqu'au 18-08 ; deux écrans ont été
 * supprimés (« Bienvenue » et « Le brief »), et laisser 6 aurait montré deux
 * points qui ne s'allument jamais — une jauge qui n'atteint pas son bout se
 * lit comme un parcours inachevé. */
const TOTAL_STEPS = 4;

/**
 * Palette du parcours candidat = le cockpit en SOMBRE : les tokens `.dark` de
 * `styles/globals.css`, et le bleu de marque de la librairie (`DEFAULT_BRAND`,
 * dérivé en survol et en texte). Appliquée sur le conteneur racine du parcours, et re-portée sur
 * tout contenu PORTALÉ hors de cette racine (cf. la prop `containerStyle` du
 * Modal, utilisée par `RulesSheet` et `TransactionModale`).
 *
 * 🩸 07-10-2026 : il y avait DEUX palettes — une crème à l'orange chaud pour
 * la candidature (14-07), et celle-ci, à l'accent rouge, pour le seul écran de
 * l'épreuve. Kayla : « j'aimerais bien que ça soit dans le même thème que le
 * cockpit […] c'est noir et bleu ». Le candidat voit donc, dès la landing, la
 * couleur de l'outil qu'il aura sous les yeux en shift.
 *
 * ⚠️ Recopiée et non importée : `globals.css` la porte dans une règle `.dark`,
 * qu'on ne peut pas appliquer ici sans basculer le thème de tout le cockpit
 * pour un candidat qui n'a même pas de compte. Si la palette dark du cockpit
 * bouge, ces valeurs bougent à la main — c'est le prix, et il est petit.
 * `colorScheme: dark` passe aussi les contrôles natifs (date, select) en sombre.
 *
 * 🪤 Les couleurs de SENS (`--color-valide` / `--color-attention` / `--color-erreur`)
 * n'y sont PAS recopiées : les conteneurs du parcours portent aussi la classe `dark`,
 * et c'est `.dark` de theme.css qui les donne — ainsi un changement de teinte dans
 * les jetons de la librairie atteint le parcours sans qu'on touche à ce fichier.
 */
export const CANDIDATE_THEME = {
  colorScheme: "dark",
  "--color-canvas": "#131313",
  "--color-panel": "#090909",
  "--color-elevated": "#0c0c0c",
  "--color-sunken": "#050505",
  "--color-fg": "#fafafa",
  "--color-muted": "#a3a3a3",
  "--color-faint": "#525252",
  "--color-line": "#1a1a1a",
  "--color-line-strong": "#262626",
  "--color-accent": "#fafafa",
  "--color-accent-hover": "#e5e5e5",
  "--color-accent-tint": "#1a1a1a",
  "--color-accent-fg": "#050505",
  // Le bleu de MARQUE, calculé par la librairie (le même que celui du thème) : plus aucune teinte écrite ici.
  "--color-mark": DEFAULT_BRAND,
  "--color-mark-hover": deriveHover(DEFAULT_BRAND),
  "--color-mark-fg": contrastFg(DEFAULT_BRAND),
} as CSSProperties;

/** L'écran de l'épreuve porte la MÊME palette depuis le 07-10 (cf. plus haut) ;
 *  le nom reste pour ses appelants. */
export const POSTE_THEME = CANDIDATE_THEME;

interface PhoneFrameProps {
  /** Sous-titre d'étape — conservé dans le contrat mais plus affiché (en-tête
   *  retiré). Gardé pour ne pas casser les appelants. */
  subtitle: string;
  /** Index d'étape 0..7 — pilote la barre de progression. */
  stepIndex: number;
  children: ReactNode;
}

export function PhoneFrame({ stepIndex, children }: PhoneFrameProps) {
  return (
    <div
      style={CANDIDATE_THEME}
      data-theme="glass"
      className="dark h-dvh overflow-y-auto overscroll-contain bg-canvas text-fg"
    >
      {/* 🩸 La marge du haut vit ICI, pas sur le scroller (07-10-2026). Posée
          sur le scroller, elle décalait la barre `sticky top-0` : un sticky se
          colle sous le padding de son scroller, donc plus bas, et le
          formulaire défilait dans ce trou AU-DESSUS de la barre (Kayla : « le
          truc démarre en haut, il descend avec »). */}
      <div className="@container/panel mx-auto flex min-h-full w-full max-w-[520px] flex-col">
        <div
          className="sticky top-0 z-10 bg-canvas/80 px-4 pb-3 pt-4 backdrop-blur-md"
          style={{ paddingTop: "max(1rem, env(safe-area-inset-top))" }}
          aria-hidden
        >
          <Jauge valeur={stepIndex + 1} max={TOTAL_STEPS} taille="sm" />
        </div>
        <div
          className="flex flex-1 flex-col gap-4 px-4 pb-8 pt-2"
          style={{ paddingBottom: "max(2rem, env(safe-area-inset-bottom))" }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * CadreDuPoste — le chrome du SEUL écran de l'épreuve de vente.
 *
 * Pourquoi un second cadre plutôt qu'une variante de `PhoneFrame` : les deux
 * ne partagent que la barre de progression. `PhoneFrame` est une colonne étroite
 * (520 px) centrée et scrollable, faite pour lire ; l'épreuve est un poste de
 * travail — pleine largeur, hauteur fixe, et c'est le FIL qui scrolle, pas la
 * page. Mélanger les deux dans un composant à drapeau donnait un composant qui
 * ne sait plus lequel il est.
 *
 * Pas de `min-h-dvh` mais `h-dvh` + `min-h-0` en cascade : sans ça la colonne
 * du fil ne peut pas calculer sa hauteur, et le composeur se fait pousser hors
 * de l'écran dès que la conversation dépasse.
 */
export function CadreDuPoste({ stepIndex, children }: PhoneFrameProps) {
  return (
    <div
      style={POSTE_THEME}
      data-theme="glass"
      className="dark flex h-dvh flex-col overflow-hidden bg-canvas text-fg"
    >
      <div className="shrink-0 px-4 pb-3 pt-3.5" aria-hidden>
        <Jauge valeur={stepIndex + 1} max={TOTAL_STEPS} taille="sm" />
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">{children}</div>
    </div>
  );
}
